import { forwardRef } from "react";
import { getNutritionTargetsForPlanDay } from "../../../../utils/nutrition-helpers";
import LogBodyMeasurementsCard from "./log-cards/LogBodyMeasurementsCard";
import LogFoodLogCard from "./log-cards/LogFoodLogCard";
import LogHabitsCard from "./log-cards/LogHabitsCard";
import LogMealPhotosCard from "./log-cards/LogMealPhotosCard";
import LogNutritionCard from "./log-cards/LogNutritionCard";
import LogPeriodCard from "./log-cards/LogPeriodCard";
import LogProgressPhotosCard from "./log-cards/LogProgressPhotosCard";
import LogReadinessCard from "./log-cards/LogReadinessCard";
import LogSessionRpeCard from "./log-cards/LogSessionRpeCard";
import LogTrainingCard from "./log-cards/LogTrainingCard";
import LogVitalsCard from "./log-cards/LogVitalsCard";

// Extracted so LogView.js's sticky-header clone (useScrollspyStickyHeader)
// can render the exact same date-label markup instead of duplicating the
// day/name formatting by hand. Same naming convention as the Nutrition
// Progress Log view's own LogDayHeaderLabel (js/.../nutrition/.../LogGrid.js).
export const LogDayHeaderLabel = ({ day }) => (
  <>
    <span className="day-number">{day.format("D")}</span>
    <span className="day-name">{day.format("ddd")}</span>
  </>
);

const LogWeekRow = forwardRef(function LogWeekRow(
  {
    weekDays,
    collapsed,
    activeFilters,
    units,
    locals,
    repMaxPR,
    femaleCycleDays,
  },
  rowRef
) {
  const show = (key) => !activeFilters || activeFilters.includes(key);

  const slotDefs = [
    {
      key: "training",
      hasContent: () => true,
      render: (d) => (
        <LogTrainingCard
          exercises={d.exercises}
          prExerciseKeys={d.prExerciseKeys}
          repMaxPR={repMaxPR}
          dayKey={d.dayKey}
        />
      ),
    },
    {
      key: "sessionRpe",
      hasContent: () => weekDays.some((d) => d.dailyStat?.sessionRPE > 0),
      render: (d) => <LogSessionRpeCard dailyStat={d.dailyStat} />,
    },
    {
      key: "nutrition",
      hasContent: () =>
        weekDays.some((d) => {
          if (
            getNutritionTargetsForPlanDay(
              d.nutritionPlans,
              d.dayKey,
              d.exercises
            )
          ) {
            return true;
          }
          const n = d.nutritionSummary?.nutrients;
          return !!(n?.calories || n?.proteins || n?.carbs || n?.fats);
        }),
      render: (d) => (
        <LogNutritionCard
          nutritionSummary={d.nutritionSummary}
          nutritionPlans={d.nutritionPlans}
          dayKey={d.dayKey}
          exercises={d.exercises}
        />
      ),
    },
    {
      key: "foodLog",
      hasContent: () => weekDays.some((d) => (d.foodLogs?.length ?? 0) > 0),
      render: (d) => <LogFoodLogCard foodLogs={d.foodLogs} />,
    },
    {
      key: "habits",
      hasContent: () =>
        weekDays.some((d) => (d.habitLogs?.accountabilities?.length ?? 0) > 0),
      render: (d) => <LogHabitsCard habitLogs={d.habitLogs} />,
    },
    {
      key: "period",
      hasContent: () =>
        (femaleCycleDays?.length ?? 0) > 0 &&
        weekDays.some((d) =>
          femaleCycleDays.some(
            (c) => c.date === d.dayKey && c.type !== "predicted"
          )
        ),
      render: (d) => (
        <LogPeriodCard femaleCycleDays={femaleCycleDays} dayKey={d.dayKey} />
      ),
    },
    {
      key: "readiness",
      hasContent: () =>
        weekDays.some((d) => {
          const s = d.dailyStat?.readiness?.score ?? d.dailyStat?.readiness;
          return !!s || s === 0;
        }),
      render: (d) => <LogReadinessCard dailyStat={d.dailyStat} />,
    },
    {
      key: "vitals",
      hasContent: () => weekDays.some((d) => !!d.dailyStat),
      render: (d) => <LogVitalsCard dailyStat={d.dailyStat} units={units} />,
    },
    {
      key: "measurements",
      hasContent: () =>
        weekDays.some(
          (d) =>
            Object.keys(d.bodyMeasurements?.bodyMeasurements ?? {}).length > 0
        ),
      render: (d) => (
        <LogBodyMeasurementsCard
          bodyMeasurements={d.bodyMeasurements}
          units={units}
        />
      ),
    },
    {
      key: "photos",
      hasContent: () =>
        weekDays.some(
          (d) =>
            Object.values(d.progressPhotos?.bodyPhotos ?? {}).filter(
              (v) => v?.s3Path
            ).length > 0
        ),
      render: (d) => (
        <LogProgressPhotosCard
          progressPhotos={d.progressPhotos}
          dailyStatId={d.progressPhotos?._id}
          utcDate={d.progressPhotos?.utcDate}
        />
      ),
    },
    {
      key: "mealPhotos",
      hasContent: () =>
        weekDays.some((d) =>
          Object.values(d.mealPhotos?.mealPhotos ?? {}).some(
            (meal) => (meal?.list?.length ?? 0) > 0
          )
        ),
      render: (d) => (
        <LogMealPhotosCard
          mealPhotos={d.mealPhotos}
          dailyStatId={d.mealPhotos?._id}
          utcDate={d.mealPhotos?.utcDate}
          locals={locals}
        />
      ),
    },
  ];

  const activeSlots = slotDefs.filter((s) => show(s.key) && s.hasContent());

  const columnClass = (d) =>
    [
      "log-day-column",
      !d.isCurrentMonth ? "other-month" : "",
      d.isToday ? "is-today" : "",
    ]
      .filter(Boolean)
      .join(" ");

  return (
    <div
      ref={rowRef}
      className={`log-week-row${collapsed ? " log-week-row--collapsed" : ""}`}
      style={
        !collapsed
          ? { gridTemplateRows: `repeat(${activeSlots.length + 1}, auto)` }
          : undefined
      }
    >
      {weekDays.map((d) => (
        <div key={d.dayKey} className={columnClass(d)} data-date={d.dayKey}>
          <div className="day-header">
            <LogDayHeaderLabel day={d.day} />
          </div>
          {activeSlots.map((slot) => (
            <div key={slot.key} className="day-slot">
              {slot.render(d)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
});

export default LogWeekRow;
